import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  CheckIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  FlagIcon,
  LinkIcon,
  PaperClipIcon,
  PlusIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

import { contextService } from '../../services/api';

const linkIcons = {
  event: CalendarDaysIcon,
  note: DocumentTextIcon,
  conversation: ChatBubbleLeftRightIcon,
  document: PaperClipIcon,
  decision: CheckIcon,
  task: FlagIcon,
};

const ContextDetailPage = () => {
  const { contextId } = useParams();
  const queryClient = useQueryClient();
  const [editingOutcome, setEditingOutcome] = useState(false);
  const [outcome, setOutcome] = useState('');

  const { data: context, isLoading, error, refetch } = useQuery({
    queryKey: ['context', contextId],
    queryFn: () => contextService.getContext(contextId),
  });

  const updateMutation = useMutation({
    mutationFn: (updates) => contextService.updateContext(contextId, updates),
    onSuccess: (updated) => {
      queryClient.setQueryData(['context', contextId], updated);
      queryClient.invalidateQueries({ queryKey: ['contexts'] });
      queryClient.invalidateQueries({ queryKey: ['today'] });
      setEditingOutcome(false);
      toast.success('Context updated');
    },
    onError: (updateError) => toast.error(updateError.response?.data?.detail || 'Could not update context'),
  });

  const startEditingOutcome = () => {
    setOutcome(context?.desired_outcome || '');
    setEditingOutcome(true);
  };

  if (isLoading) {
    return (
      <div className="app-page">
        <div className="app-page-inner max-w-6xl space-y-4">
          <div className="h-28 animate-pulse rounded-lg bg-[#e8ece8]" />
          <div className="h-96 animate-pulse rounded-lg bg-[#e8ece8]" />
        </div>
      </div>
    );
  }

  if (error || !context) {
    return (
      <div className="app-page">
        <div className="app-page-inner max-w-3xl">
          <section className="surface-panel px-6 py-16 text-center">
            <h1 className="text-lg font-bold">Context unavailable</h1>
            <p className="mt-2 text-sm text-[#6b766f]">This context could not be loaded.</p>
            <button onClick={() => refetch()} className="secondary-button mt-5">Try again</button>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page">
      <div className="app-page-inner max-w-6xl">
        <Link to="/contexts" className="mb-5 inline-flex min-h-9 items-center gap-2 text-xs font-bold text-[#5e6a63] hover:text-[#17211c]">
          <ArrowLeftIcon className="h-4 w-4" />
          All contexts
        </Link>

        <header className="page-header">
          <div>
            <div className="eyebrow">
              <LinkIcon className="h-4 w-4" />
              {context.context_type}
            </div>
            <h1 className="page-title">{context.title}</h1>
            <p className="page-description">{context.description || 'No background has been added yet.'}</p>
          </div>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('timelyzer:universal-capture', { detail: { contextId: context.id } }))}
            className="primary-button"
          >
            <PlusIcon className="h-4 w-4" />
            Add context
          </button>
        </header>

        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          {[
            ['Connected items', context.link_count, LinkIcon, 'bg-[#dcece4] text-[#246b52]'],
            ['People', context.people?.length || 0, UsersIcon, 'bg-[#e2edf6] text-[#32699a]'],
            ['Goals', context.goals?.length || 0, FlagIcon, 'bg-[#f7edda] text-[#996617]'],
          ].map(([label, value, Icon, tone]) => (
            <div key={label} className="surface-panel flex items-center gap-3 p-4">
              <span className={`flex h-10 w-10 items-center justify-center rounded-md ${tone}`}>
                <Icon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xl font-bold">{value}</p>
                <p className="text-xs font-semibold text-[#758078]">{label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <section className="surface-panel overflow-hidden">
            <div className="flex items-center justify-between border-b border-[#e2e7e3] px-5 py-4">
              <div>
                <p className="text-[11px] font-bold uppercase text-[#7c8780]">Continuous thread</p>
                <h2 className="mt-1 text-base font-bold">Context timeline</h2>
              </div>
              <span className="status-pill status-pill-success">{context.status}</span>
            </div>

            {context.links?.length ? (
              <div>
                {context.links.map((link) => {
                  const Icon = linkIcons[link.item_type] || LinkIcon;
                  return (
                    <article key={link.id} className="grid gap-3 border-b border-[#e9edea] p-5 last:border-0 sm:grid-cols-[40px_minmax(0,1fr)_auto]">
                      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[#edf2ee] text-[#246b52]">
                        <Icon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-sm font-bold">{link.title || `${link.item_type} context`}</h3>
                          {link.item_type === 'event' && link.item_id && <Link className="text-xs font-semibold text-[#246b52] underline" to={`/meetings/${link.item_id}`}>Open meeting workspace</Link>}
                          {link.provenance?.resource === 'mail_thread' && link.item_id && <Link className="text-xs underline text-[#246b52]" to={`/mail/threads/${link.item_id}`}>Open email conversation</Link>}
                          <span className="status-pill bg-[#edf0ed] text-[#68736d]">{link.source}</span>
                        </div>
                        {link.excerpt && <p className="mt-2 text-sm leading-6 text-[#66716a]">{link.excerpt}</p>}
                        <p className="mt-3 flex items-center gap-2 text-xs font-semibold text-[#7a857e]">
                          <ShieldCheckIcon className="h-4 w-4" />
                          Source preserved · {link.role}
                        </p>
                      </div>
                      <time className="text-xs text-[#8a948e]">
                        {format(new Date(link.occurred_at || link.created_at), 'MMM d, h:mm a')}
                      </time>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="px-6 py-20 text-center">
                <LinkIcon className="mx-auto h-8 w-8 text-[#4d896d]" />
                <h3 className="mt-3 text-sm font-bold">No connected information yet</h3>
                <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-[#6b766f]">
                  Capture a note, decision, transcript excerpt, or preparation item directly into this context.
                </p>
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('timelyzer:universal-capture', { detail: { contextId: context.id } }))}
                  className="primary-button mt-5"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add first item
                </button>
              </div>
            )}
          </section>

          <aside className="space-y-4">
            <section className="surface-panel p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-bold uppercase text-[#7c8780]">Desired outcome</p>
                  <h2 className="mt-1 text-sm font-bold">What should happen?</h2>
                </div>
                <FlagIcon className="h-5 w-5 text-[#996617]" />
              </div>

              {editingOutcome ? (
                <div className="mt-4">
                  <textarea
                    value={outcome}
                    onChange={(event) => setOutcome(event.target.value)}
                    className="field-input resize-none"
                    rows={4}
                    autoFocus
                  />
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => updateMutation.mutate({ desired_outcome: outcome })}
                      disabled={updateMutation.isPending}
                      className="primary-button min-h-9 px-3 text-xs"
                    >
                      {updateMutation.isPending ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                      Save
                    </button>
                    <button onClick={() => setEditingOutcome(false)} className="secondary-button min-h-9 px-3 text-xs">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="mt-4 text-sm leading-6 text-[#5f6b64]">
                    {context.desired_outcome || 'No desired outcome has been defined. Timelyzer will have less signal for prioritization.'}
                  </p>
                  {context.plan_id ? <Link to={`/plans/${context.plan_id}`} className="secondary-button mt-4 min-h-9 w-full text-xs">Open plan and steps</Link> : <Link to={`/plans?context=${context.id}`} className="secondary-button mt-4 min-h-9 w-full text-xs">Add a step-by-step plan</Link>}
                  {!context.plan_id && <button onClick={startEditingOutcome} className="secondary-button mt-4 min-h-9 w-full text-xs">
                    {context.desired_outcome ? 'Edit outcome' : 'Define outcome'}
                  </button>}
                </>
              )}
            </section>

            <section className="surface-panel bg-[#f4f8f5] p-5">
              <SparklesIcon className="h-5 w-5 text-[#246b52]" />
              <h2 className="mt-3 text-sm font-bold">Context briefing</h2>
              <p className="mt-2 text-xs leading-5 text-[#66766d]">
                {context.summary || 'A briefing will become more useful as notes, events, decisions, and conversations are connected.'}
              </p>
            </section>

            <section className="surface-panel p-5">
              <h2 className="text-sm font-bold">Source coverage</h2>
              <div className="mt-4 space-y-3">
                {['event', 'note', 'conversation', 'document'].map((type) => {
                  const connected = context.links?.some((link) => link.item_type === type);
                  return (
                    <div key={type} className="flex items-center justify-between">
                      <span className="text-xs font-semibold capitalize text-[#5f6b64]">{type}</span>
                      <span className={`status-pill ${connected ? 'status-pill-success' : 'bg-[#edf0ed] text-[#7a857e]'}`}>
                        {connected ? 'Connected' : 'Not connected'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ContextDetailPage;
